import { Alert, Button, Stack } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';

import { useAuth } from '../../useAuth';
import { icsApi } from '../../utils/axios/api';
import {
  getSalesDemoMode,
  reloadProjectWorkspace,
  salesDemoModeInitialized,
  salesDemoStorageKey,
  setSalesDemoMode,
} from './salesDemoMode';

/** Do not mount project routes until the server has confirmed presentation scope. */
export function SalesDemoGate({ children }: { children: ReactNode }) {
  const { authenticated, token } = useAuth();
  const queryClient = useQueryClient();
  const enabled = authenticated || !!token;
  const mode = useQuery({
    queryKey: ['sales-demo-mode'],
    enabled,
    queryFn: async () => {
      const { data } = await icsApi.get<{ enabled: boolean }>('/sales-demo/mode');
      if (salesDemoModeInitialized() && getSalesDemoMode() !== data.enabled) {
        reloadProjectWorkspace();
        throw new Error('Project mode changed');
      }
      setSalesDemoMode(data.enabled);
      return data;
    },
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === salesDemoStorageKey) {
        queryClient.clear();
        reloadProjectWorkspace();
      }
    };
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) reloadProjectWorkspace();
    };
    const leaving = () => {
      document.documentElement.style.visibility = 'hidden';
    };
    window.addEventListener('storage', changed);
    window.addEventListener('pageshow', restored);
    window.addEventListener('pagehide', leaving);
    return () => {
      window.removeEventListener('storage', changed);
      window.removeEventListener('pageshow', restored);
      window.removeEventListener('pagehide', leaving);
    };
  }, [queryClient]);

  if (enabled && mode.isError) {
    return (
      <Stack p={3} gap={2}>
        <Alert severity="error">Could not verify project mode.</Alert>
        <Button onClick={() => mode.refetch()}>Retry</Button>
      </Stack>
    );
  }
  if (enabled && !mode.data) return null;
  return children;
}
